import React, { useState, useRef, useEffect } from 'react';
import ViewImageModal from '../shared/ViewImageModal';
import ImageCropModal from '../shared/ImageCropModal';

const ProfileHeader = ({ artist, profilePic, onConfirmCrop }) => {
  const fileInputRef = useRef(null);
  const dropdownRef = useRef(null);
  const [isUploading, setIsUploading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [cropImage, setCropImage] = useState(null);

  const handleAvatarClick = () => {
    if (profilePic && !profilePic.includes('ui-avatars.com')) {
      setShowDropdown(true);
    } else {
      fileInputRef.current?.click();
    }
  };

  const handleViewImage = () => {
    setShowDropdown(false);
    setShowViewModal(true);
  };

  const handleModifyImage = () => {
    setShowDropdown(false);
    fileInputRef.current?.click();
  };

  const handleCloseViewModal = () => {
    setShowViewModal(false);
  };

  const handleChangeFromModal = () => {
    setShowViewModal(false);
    fileInputRef.current?.click();
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showDropdown]);

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const imageUrl = URL.createObjectURL(file);
    setCropImage(imageUrl);
  };

  const handleCropConfirm = async (blob) => {
    setIsUploading(true);
    try {
      await onConfirmCrop(blob);
    } finally {
      setIsUploading(false);
      setCropImage(null);
    }
  };

  const handleCropCancel = () => {
    setCropImage(null);
  };

  const formatNumber = (num) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num?.toString() || '0';
  };

  // Fallback stats (à remplacer par de vraies données)
  const stats = {
    releases: artist?.releases_count || 0,
    followers: artist?.followers_count || 0,
    revenue: artist?.total_revenue || 0,
  };

  return (
    <div className="settings-profile-header">
      <div
        className="profile-header-cover"
        style={artist?.banner_url ? {
          backgroundImage: `linear-gradient(135deg, rgba(17, 24, 39, 0.45), rgba(120, 0, 0, 0.35)), url(${artist.banner_url})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        } : undefined}
      ></div>
      
      <div className="profile-header-content">
        <div className="profile-header-avatar-section">
          <div className="profile-header-avatar-wrapper" ref={dropdownRef}>
            <div className="profile-header-avatar" onClick={handleAvatarClick}>
              <img 
                src={profilePic || `https://ui-avatars.com/api/?name=${encodeURIComponent(artist?.name || 'A')}&background=780000&color=fff&size=300`}
                alt={artist?.name}
              />
              <div className="profile-header-avatar-upload">
                {isUploading ? (
                  <i className="fa-solid fa-spinner fa-spin"></i>
                ) : (
                  <i className="fa-solid fa-camera"></i>
                )}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="d-none"
                onChange={handleFileChange}
              />
            </div>
            
            {showDropdown && (
              <div className="avatar-dropdown">
                <button className="avatar-dropdown-item" onClick={handleViewImage}>
                  <i className="fa-solid fa-eye"></i>
                  Voir image
                </button>
                <button className="avatar-dropdown-item" onClick={handleModifyImage}>
                  <i className="fa-solid fa-pen"></i>
                  Modifier
                </button>
              </div>
            )}
          </div>

          <div className="profile-header-info">
            <h1 className="profile-header-name">
              {artist?.name || 'Artiste'}
              {artist?.is_verified && (
                <span className="profile-verified-badge">
                  <i className="fa-solid fa-check"></i>
                </span>
              )}
            </h1>
            <p className="profile-header-username">
              @{artist?.slug || artist?.name?.toLowerCase().replace(/\s+/g, '')}
            </p>
          </div>

          <div className="profile-header-actions">
            <button
              className="btn-outline"
              onClick={() => artist?.slug && window.open(`https://passiio.shop/artist/${artist.slug}`, '_blank', 'noopener,noreferrer')}
              disabled={!artist?.slug}
            >
              <i className="fa-solid fa-eye"></i>
              Voir le profil
            </button>
          </div>
        </div>

        {!!artist?.is_pro && (
          <div className="mb-3">
            <span className="profile-pro-badge">
              <i className="fa-solid fa-gem"></i>
              Membre Pro
            </span>
          </div>
        )}

        <div className="profile-header-stats">
          <div className="profile-stat">
            <p className="profile-stat-value">{formatNumber(stats.releases)}</p>
            <p className="profile-stat-label">Releases</p>
          </div>
          <div className="profile-stat">
            <p className="profile-stat-value">{formatNumber(stats.followers)}</p>
            <p className="profile-stat-label">Followers</p>
          </div>
          <div className="profile-stat">
            <p className="profile-stat-value">{formatNumber(stats.revenue)} Ar</p>
            <p className="profile-stat-label">Revenus</p>
          </div>
        </div>
      </div>
      
      {showViewModal && (
        <ViewImageModal
          image={profilePic}
          onClose={handleCloseViewModal}
          onChange={handleChangeFromModal}
        />
      )}
      
      {cropImage && (
        <ImageCropModal
          image={cropImage}
          onConfirm={handleCropConfirm}
          onCancel={handleCropCancel}
          isUploading={isUploading}
        />
      )}
    </div>
  );
};

export default ProfileHeader;
